/**
 * Consolidated Cashflow Forecast — aggregates per-RS waterfalls into a single
 * month-by-month treasury view. Pure function, no DB writes.
 *
 * Reuses the locked `buildWaterfall()` engine — does NOT modify any financial logic.
 * Inception + Interim + SD-Refund + Final-GST + Rent rows are all folded into the
 * calendar month they belong to (so no event noise — just months).
 */

import { Invoice } from "../types/invoice";
import { Schedule } from "../store/appStore";
import { NoticeOfAssignment } from "../types/schema";
import { buildWaterfall, WaterfallRow } from "../lib/cashflow-waterfall";
import { isActiveRS } from "../lib/portfolio-stats";
import { addMonths, format, parseISO, startOfMonth } from "date-fns";

export type Granularity = "monthly" | "quarterly";

export interface ConsolidatedFilters {
  granularity: Granularity;
  fy: string | "all"; // "FY24-25" | "all"
  customerIds: string[]; // empty = all
  lenders: string[]; // empty = all
}

/** Query params for GET /rental-schedule/get-consolidated-rental-schedule-cashflow */
export function buildConsolidatedCashflowApiParams(
  filters: ConsolidatedFilters
): Record<string, string> {
  const params: Record<string, string> = {
    granularity: filters.granularity,
    fy: filters.fy,
  };
  if (filters.customerIds.length > 0) {
    params.customerIds = filters.customerIds.join(",");
  }
  return params;
}

export interface ConsolidatedRow {
  periodKey: string; // yyyy-MM (monthly) or yyyy-Qn (quarterly)
  periodLabel: string;
  startDate: string; // ISO first day of period
  activeRS: number;
  newRS: number;
  maturingRS: number;
  rentals: number; // customerPays from Rent/Inception (incl GST less TDS in their convention)
  sdNet: number;
  pfIn: number;
  lenderIn: number;
  vendorPay: number;
  outputGST: number;
  itcUtilised: number;
  netGstPayable: number;
  tds: number;
  cmTds: number;
  cashflowToAstrafin: number;
  cmCashflowToAstrafin: number;
  cashflowToLender: number;
  isPast: boolean;
}


export interface ConsolidatedTotals {
  rentals: number;
  sdNet: number;
  pfIn: number;
  lenderIn: number;
  vendorPay: number;
  outputGST: number;
  itcUtilised: number;
  netGstPayable: number;
  tds: number;
  cmTds: number;
  cashflowToAstrafin: number;
  cmCashflowToAstrafin: number;
  cashflowToLender: number;
}


export interface ConsolidatedKPIs {
  totalInvoiceValue: number;
  cashFlowToAstrafin: number;
  quarterlyInvoiceValue: number;
  quarterlyCashFlowToAstrafin: number;
  /** Optional badge text from API, e.g. "Q2 FY 2026–27" */
  quarterLabel: string | null;
}

export const EMPTY_CONSOLIDATED_KPIS: ConsolidatedKPIs = {
  totalInvoiceValue: 0,
  cashFlowToAstrafin: 0,
  quarterlyInvoiceValue: 0,
  quarterlyCashFlowToAstrafin: 0,
  quarterLabel: null,
};

const kpiNum = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const kpiPick = (
  obj: Record<string, unknown>,
  camel: string,
  snake: string
) => obj[camel] ?? obj[snake];

/**
 * Normalize GET /rental-schedule/get-consolidated-rental-schedule-cashflow-kpis
 * (or a `kpis` block nested on the cashflow payload).
 */
export function normalizeConsolidatedCashflowKpis(
  payload: unknown
): ConsolidatedKPIs {
  const root =
    payload && typeof payload === "object" && "data" in payload
      ? (payload as { data?: unknown }).data
      : payload;

  if (!root || typeof root !== "object") return { ...EMPTY_CONSOLIDATED_KPIS };

  const body = root as Record<string, unknown>;
  const raw =
    body.kpis && typeof body.kpis === "object"
      ? (body.kpis as Record<string, unknown>)
      : body;

  const quarterLabel =
    (typeof kpiPick(raw, "quarterLabel", "quarter_label") === "string"
      ? String(kpiPick(raw, "quarterLabel", "quarter_label")).trim()
      : "") || null;

  return {
    totalInvoiceValue: kpiNum(
      kpiPick(raw, "totalInvoiceValue", "total_invoice_value")
    ),
    cashFlowToAstrafin: kpiNum(
      kpiPick(raw, "cashFlowToAstrafin", "cash_flow_to_astrafin")
    ),
    quarterlyInvoiceValue: kpiNum(
      kpiPick(raw, "quarterlyInvoiceValue", "quarterly_invoice_value")
    ),
    quarterlyCashFlowToAstrafin: kpiNum(
      kpiPick(
        raw,
        "quarterlyCashFlowToAstrafin",
        "quarterly_cash_flow_to_astrafin"
      )
    ),
    quarterLabel,
  };
}

/** Query params for consolidated cashflow KPI headline cards (current period). */
export function buildConsolidatedCashflowKpiParams(
  filters: Pick<ConsolidatedFilters, "customerIds" | "lenders">
): Record<string, string> | undefined {
  const params: Record<string, string> = {};
  if (filters.customerIds.length > 0) {
    params.customerIds = filters.customerIds.join(",");
  }
  if (filters.lenders.length > 0) {
    params.lenders = filters.lenders.join(",");
  }
  return Object.keys(params).length > 0 ? params : undefined;
}

export interface ConsolidatedResult {
  rows: ConsolidatedRow[];
  totals: ConsolidatedTotals;
  kpis: ConsolidatedKPIs;
  breakdownByRS: Map<string, Map<string, ConsolidatedRow>>; // rsId -> periodKey -> row
}

// --- Indian FY helpers ---
export const getFYLabel = (d: Date): string => {
  const y = d.getFullYear();
  const startYear = d.getMonth() >= 3 ? y : y - 1;
  return `FY${String(startYear).slice(2)}-${String(startYear + 1).slice(2)}`;
};

/** Portfolio cashflow filter: previous FY, current FY (middle), next FY — exactly 3. */
export const getPortfolioFYOptions = (): string[] => {
  const today = new Date();
  const currentStartYear =
    today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;

  return [-1, 0, 1].map((offset) => {
    const y = currentStartYear + offset;
    return `FY${String(y).slice(2)}-${String(y + 1).slice(2)}`;
  });
};

export const fyBounds = (fy: string): { start: Date; end: Date } => {
  // fy like "FY24-25"
  const m = fy.match(/FY(\d{2})-(\d{2})/);
  const startYear = m ? 2000 + parseInt(m[1], 10) : new Date().getFullYear();
  return {
    start: new Date(startYear, 3, 1),
    end: new Date(startYear + 1, 2, 31, 23, 59, 59),
  };
};

export const fyQuarter = (d: Date): number => {
  // Apr-Jun=1, Jul-Sep=2, Oct-Dec=3, Jan-Mar=4
  const m = d.getMonth();
  if (m >= 3 && m <= 5) return 1;
  if (m >= 6 && m <= 8) return 2;
  if (m >= 9 && m <= 11) return 3;
  return 4;
};

/** Badge label like "Q2 FY 2026–27" for the current Indian FY quarter. */
export const getCurrentFYQuarterLabel = (d: Date = new Date()): string => {
  const q = fyQuarter(d);
  const startYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `Q${q} FY ${startYear}–${String(startYear + 1).slice(2)}`;
};

const periodKeyFor = (d: Date, g: Granularity): { key: string; label: string; start: Date } => {
  if (g === "quarterly") {
    const fy = getFYLabel(d);
    const q = fyQuarter(d);
    // start month of quarter
    const startMonth = q === 1 ? 3 : q === 2 ? 6 : q === 3 ? 9 : 0;
    const startYear = q === 4 ? d.getFullYear() : (d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear());
    // Q4 spans Jan-Mar (calendar year = startYear of FY + 1)
    const sy = q === 4 ? (d.getMonth() >= 3 ? d.getFullYear() + 1 : d.getFullYear()) : startYear;
    const start = new Date(sy, startMonth, 1);
    return { key: `${fy}-Q${q}`, label: `${fy} Q${q}`, start };
  }
  const start = startOfMonth(d);
  return {
    key: format(start, "yyyy-MM"),
    label: format(start, "MMM yyyy"),
    start,
  };
};

const blankRow = (key: string, label: string, start: Date): ConsolidatedRow => ({
  periodKey: key,
  periodLabel: label,
  startDate: format(start, "yyyy-MM-dd"),
  activeRS: 0,
  newRS: 0,
  maturingRS: 0,
  rentals: 0,
  sdNet: 0,
  pfIn: 0,
  lenderIn: 0,
  vendorPay: 0,
  outputGST: 0,
  itcUtilised: 0,
  netGstPayable: 0,
  tds: 0,
  cmTds: 0,
  cashflowToAstrafin: 0,
  cmCashflowToAstrafin: 0,
  cashflowToLender: 0,
  isPast: start <= new Date(),
});


export function buildConsolidatedCashflow(
  schedules: Schedule[],
  noAs: NoticeOfAssignment[],
  invoices: Invoice[],
  filters: ConsolidatedFilters
): ConsolidatedResult {
  const today = new Date();
  const g = filters.granularity;

  // 1. Filter eligible RS
  const eligible = schedules.filter((s) => {
    if (!isActiveRS(s)) return false;
    if (filters.customerIds.length && !filters.customerIds.includes(s.customerID)) return false;
    if (filters.lenders.length) {
      const noa = noAs.find((n) => n.scheduleId === s.id);
      if (!noa?.lender || !filters.lenders.includes(noa.lender)) return false;
    }
    return true;
  });

  // 2. Determine period bounds
  let rangeStart: Date | null = null;
  let rangeEnd: Date | null = null;
  if (filters.fy !== "all") {
    const b = fyBounds(filters.fy);
    rangeStart = b.start;
    rangeEnd = b.end;
  }

  // 3. Build per-RS waterfalls, distribute rows into period buckets
  const periodMap = new Map<string, ConsolidatedRow>();
  const breakdownByRS = new Map<string, Map<string, ConsolidatedRow>>();
  const rsPeriods = new Map<string, { firstKey: string; lastKey: string }>();

  for (const rs of eligible) {
    const noa = noAs.find((n) => n.scheduleId === rs.id);
    const wf = buildWaterfall({ schedule: rs, noa, invoices });
    if (!wf.rows.length) continue;

    const perRsMap = new Map<string, ConsolidatedRow>();
    let firstKey: string | null = null;
    let lastKey: string | null = null;

    for (const r of wf.rows) {
      const d = parseISO(r.monthDate);
      if (rangeStart && d < rangeStart) continue;
      if (rangeEnd && d > rangeEnd) continue;

      const { key, label, start } = periodKeyFor(d, g);
      if (!periodMap.has(key)) periodMap.set(key, blankRow(key, label, start));
      if (!perRsMap.has(key)) perRsMap.set(key, blankRow(key, label, start));

      const bucket = periodMap.get(key)!;
      const rsBucket = perRsMap.get(key)!;

      const netGst = Math.max(0, r.outputGST - r.itcUsed);
      const apply = (b: ConsolidatedRow) => {
        b.rentals += r.customerPays;
        b.sdNet += r.sdInOut;
        b.pfIn += r.pfIn;
        b.lenderIn += r.lenderIn;
        b.vendorPay += r.vendorPay;
        b.outputGST += r.outputGST;
        b.itcUtilised += r.itcUsed;
        b.netGstPayable += netGst;
        b.tds += r.tds;
        b.cashflowToAstrafin += r.cashflowToAstrafin;
        b.cashflowToLender += r.cashflowToLender;
      };
      apply(bucket);
      apply(rsBucket);

      if (firstKey === null || key < firstKey) firstKey = key;
      if (lastKey === null || key > lastKey) lastKey = key;
    }

    if (firstKey && lastKey) {
      rsPeriods.set(rs.id, { firstKey, lastKey });
      breakdownByRS.set(rs.id, perRsMap);

      // Mark Active RS for all periods between first..last (inclusive)
      for (const key of periodMap.keys()) {
        if (key >= firstKey && key <= lastKey) {
          const b = periodMap.get(key)!;
          b.activeRS += 1;
          if (key === firstKey) b.newRS += 1;
          if (key === lastKey) b.maturingRS += 1;
        }
      }
    }
  }

  // Need to re-count Active across periods that might not yet exist as buckets but fall within an RS's active range. Add empty period buckets to fill gaps.
  // Find global min/max
  const allKeys = Array.from(periodMap.keys()).sort();
  if (allKeys.length === 0) {
    return {
      rows: [],
      totals: emptyTotals(),
      kpis: { ...EMPTY_CONSOLIDATED_KPIS },
      breakdownByRS,
    };
  }

  // Re-do active counts properly with full range
  for (const key of allKeys) {
    const row = periodMap.get(key)!;
    row.activeRS = 0;
    row.newRS = 0;
    row.maturingRS = 0;
  }
  for (const [, span] of rsPeriods) {
    for (const key of allKeys) {
      if (key >= span.firstKey && key <= span.lastKey) {
        const b = periodMap.get(key)!;
        b.activeRS += 1;
        if (key === span.firstKey) b.newRS += 1;
        if (key === span.lastKey) b.maturingRS += 1;
      }
    }
  }

  const rows = allKeys
    .map((k) => periodMap.get(k)!)
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  // Running cumulative Astrafin & TDS (chronological)
  let runAstra = 0;
  let runTds = 0;
  for (const r of rows) {
    runAstra += r.cashflowToAstrafin;
    runTds += r.tds;
    r.cmCashflowToAstrafin = Math.round(runAstra);
    r.cmTds = Math.round(runTds);
  }

  // Same cumulative scoped to each RS breakdown
  for (const [, perRsMap] of breakdownByRS) {
    const sortedKeys = Array.from(perRsMap.keys()).sort();
    let rsRun = 0;
    let rsTds = 0;
    for (const k of sortedKeys) {
      const row = perRsMap.get(k)!;
      rsRun += row.cashflowToAstrafin;
      rsTds += row.tds;
      row.cmCashflowToAstrafin = Math.round(rsRun);
      row.cmTds = Math.round(rsTds);
    }
  }


  // 4. Totals
  const totals: ConsolidatedTotals = emptyTotals();
  for (const r of rows) {
    totals.rentals += r.rentals;
    totals.sdNet += r.sdNet;
    totals.pfIn += r.pfIn;
    totals.lenderIn += r.lenderIn;
    totals.vendorPay += r.vendorPay;
    totals.outputGST += r.outputGST;
    totals.itcUtilised += r.itcUtilised;
    totals.netGstPayable += r.netGstPayable;
    totals.tds += r.tds;
    totals.cashflowToAstrafin += r.cashflowToAstrafin;
    totals.cashflowToLender += r.cashflowToLender;
  }
  totals.cmCashflowToAstrafin = rows.length ? rows[rows.length - 1].cmCashflowToAstrafin : 0;
  totals.cmTds = rows.length ? rows[rows.length - 1].cmTds : 0;


  // 5. KPIs (use current period regardless of filter for headline)
  const fy = getFYLabel(today);
  const thisQuarterKey = `${fy}-Q${fyQuarter(today)}`;
  const thisQuarterRows =
    g === "quarterly"
      ? rows.filter((r) => r.periodKey === thisQuarterKey)
      : rows.filter((r) => {
          const d = parseISO(r.startDate);
          return fyQuarter(d) === fyQuarter(today) && getFYLabel(d) === fy;
        });

  return {
    rows,
    totals,
    kpis: {
      totalInvoiceValue: totals.rentals,
      cashFlowToAstrafin: totals.cashflowToAstrafin,
      quarterlyInvoiceValue: thisQuarterRows.reduce((s, r) => s + r.rentals, 0),
      quarterlyCashFlowToAstrafin: thisQuarterRows.reduce(
        (s, r) => s + r.cashflowToAstrafin,
        0
      ),
      quarterLabel: getCurrentFYQuarterLabel(today),
    },
    breakdownByRS,
  };
}

function emptyTotals(): ConsolidatedTotals {
  return {
    rentals: 0,
    sdNet: 0,
    pfIn: 0,
    lenderIn: 0,
    vendorPay: 0,
    outputGST: 0,
    itcUtilised: 0,
    netGstPayable: 0,
    tds: 0,
    cmTds: 0,
    cashflowToAstrafin: 0,
    cmCashflowToAstrafin: 0,
    cashflowToLender: 0,

  };
}

export const availableFYs = (schedules: Schedule[]): string[] => {
  const set = new Set<string>();
  const today = new Date();
  set.add(getFYLabel(today));
  set.add(getFYLabel(addMonths(today, 12)));
  for (const s of schedules) {
    const c = s.config?.commencementDate;
    if (c) set.add(getFYLabel(parseISO(c)));
    const tenure = s.config?.tenureMonths || 0;
    if (c && tenure) set.add(getFYLabel(addMonths(parseISO(c), tenure)));
  }
  return Array.from(set).sort();
};
